import { IconAI } from "@/assets/icons/geist/IconAI";
import { IconEarlyAccess } from "@/assets/icons/geist/IconEarlyAccess";
import { IconGitpodEngraved } from "@/assets/icons/geist/IconGitpodEngraved";
import { IconGrid } from "@/assets/icons/geist/IconGrid";
import { IconNewEnvironment } from "@/assets/icons/geist/IconNewEnvironment";
import { SidebarAgentExecutionList } from "@/components/ai/SidebarAgentExecutionList";
import { DesktopTitlebar } from "@/components/desktop/DesktopTitlebar";
import { Button } from "@/components/flexkit/Button";
import { Resizable, ResizableHandle } from "@/components/flexkit/Resizable";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { Nudges } from "@/components/nudges/Nudges";
import { OnboardingSidebarSection } from "@/components/onboarding/OnboardingSidebarSection";
import { OrgSwitcher } from "@/components/OrgSwitcher";
import { cn } from "@/components/podkit/lib/cn";
import { SidebarButton } from "@/components/SidebarButton";
import { Tooltip } from "@/components/Tooltip";
import { Flags } from "@/feature-flags";
import { useDesktop } from "@/hooks/use-desktop";
import { useFlag } from "@/hooks/use-flag";
import { useMembership } from "@/hooks/use-membership";
import { TrackLocations } from "@/hooks/use-segment";
import { useStreamEvents } from "@/hooks/use-stream-events";
import { useTheme } from "@/hooks/use-theme";
import { getPrincipal, onDidChangePrincipal } from "@/principal";
import { CreateEnvironmentModal } from "@/routes/environments/create/CreateEnvironment";
import { SidebarEnvironmentList } from "@/routes/environments/SidebarEnvironmentList";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { ChartNoAxesCombined } from "lucide-react";
import { useEffect, useState, type FC, type PropsWithChildren, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";

export const SidebarLayout: FC<{ customSidebar?: ReactNode; noPadding?: boolean }> = ({ customSidebar, noPadding }) => {
    const desktop = useDesktop();
    useStreamEvents();
    // This ensures that we trigger a re-render when the principal changes - this is needed to
    // ensure we re-render when the user is updated.
    const [, setPrincipal] = useState<string | null>(null);
    useEffect(() => {
        return onDidChangePrincipal(() => {
            setPrincipal(getPrincipal());
        });
    }, [setPrincipal]);
    return (
        <div className="app-background-resetting">
            <div className="app-background h-dvh w-dvw overflow-hidden">
                {desktop.isDesktop && <DesktopTitlebar />}
                <div className="flex h-dvh w-dvw flex-row overflow-hidden">
                    <div
                        className="hidden flex-shrink-0 min-[540px]:block"
                        data-track-location={TrackLocations.Sidebar}
                    >
                        {customSidebar ? customSidebar : <Sidebar />}
                    </div>
                    <div className="h-dvh max-h-dvh w-full flex-grow overflow-hidden p-2 md:pl-0">
                        <div
                            className={cn(
                                "h-full w-full overflow-hidden rounded-lg border-0.5 border-border-base bg-surface-01 p-0.5 shadow-content-surface dark:shadow-none",
                                { "p-0": noPadding },
                            )}
                        >
                            <MainContent noPadding={noPadding}>
                                <Outlet />
                            </MainContent>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const NoSidebar: FC = () => {
    return <div className="pr-2" />;
};

export const ResizableSidebar: FC<PropsWithChildren> = ({ children }) => {
    return (
        <Resizable initialWidth={300} maxWidth={500} minWidth={296} autoSaveId="sidebar-width" className="h-dvh">
            {children}
        </Resizable>
    );
};

export const Sidebar: FC = () => {
    const { isDesktop } = useDesktop();
    return (
        <ResizableSidebar>
            <div
                data-testid="sidebar"
                className={cn("flex h-dvh w-full flex-col pb-[6px] pl-2 pt-4", {
                    "pt-10": isDesktop,
                })}
            >
                <div className="pl-2 pr-2">
                    <SidebarHeader />
                </div>
                <ScrollArea orientation="vertical" className="mr-0.5 flex-grow pt-2">
                    <SidebarContent />
                </ScrollArea>
                <div className="pb-2">
                    <SidebarFooter />
                </div>
            </div>
            <ResizableHandle className="top-[16px] mr-[1px] h-[calc(100dvh-32px)]" />
        </ResizableSidebar>
    );
};

const SidebarHeader: FC = () => {
    const [showNewEnvModal, setShowNewEnvModal] = useState(false);
    return (
        <>
            <div className="flex flex-row justify-between gap-2">
                <div className="flex min-w-16 rounded-lg hover:bg-surface-03">
                    <OrgSwitcher data-track-location={TrackLocations.SidebarOrgSwitcher} />
                </div>

                <Tooltip content="Create environment">
                    <Button
                        id="new-environment"
                        variant={"secondary"}
                        size={"md"}
                        LeadingIcon={IconNewEnvironment}
                        onClick={() => setShowNewEnvModal(true)}
                        aria-label="Create environment"
                    />
                </Tooltip>
            </div>
            {showNewEnvModal && (
                <CreateEnvironmentModal
                    onClose={() => setShowNewEnvModal(false)}
                    data-track-location={TrackLocations.SidebarCreateEnvironmentModal}
                />
            )}
        </>
    );
};

export const SidebarFooter: FC = () => {
    const { effectiveTheme: variant } = useTheme();
    return (
        <>
            <div className="mt-4 flex min-h-6 w-full flex-col items-center justify-center gap-[4px]">
                <Nudges />
                <IconGitpodEngraved variant={variant} className="block h-6 w-6 shadow-engraving" />
                <IconEarlyAccess variant={variant} className="dark:fill-surface-01/20" />
            </div>
        </>
    );
};

const SidebarContent: FC = () => {
    const location = useLocation();
    const { value: isOnaEnabled } = useFlag(Flags.OnaAIEnabled, false);
    const { value: isInsightsEnabled } = useFlag(Flags.InsightsEnabled, false);

    const { membership } = useMembership();
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;

    return (
        <>
            <div className="flex flex-col gap-1 pl-2">
                <OnboardingSidebarSection />
                {isOnaEnabled && (
                    <SidebarButton
                        icon={<IconAI size="lg" />}
                        label="Home"
                        active={location.pathname === "/ai"}
                        to="/ai"
                    />
                )}
                <SidebarButton
                    icon={<IconGrid size="lg" />}
                    label="Projects"
                    active={location.pathname === "/projects"}
                    to="/projects"
                />
                {isInsightsEnabled && isOrgAdmin && (
                    <SidebarButton
                        icon={<ChartNoAxesCombined size={24} />}
                        label="Insights"
                        active={location.pathname === "/insights"}
                        to="/insights"
                    />
                )}
                <div className="m-2 border-t-[1px] border-surface-invert/10" />
                {isOnaEnabled && <SidebarAgentExecutionList />}
                <SidebarEnvironmentList />
            </div>
        </>
    );
};

const MainContent: FC<PropsWithChildren<{ noPadding?: boolean }>> = ({ children, noPadding }) => {
    const Comp = noPadding ? "div" : ScrollArea;
    return (
        <Comp orientation="both" className={cn("h-full w-full max-w-full", { "p-0": noPadding, "p-6": !noPadding })}>
            {children}
        </Comp>
    );
};
